// On a card and atop a thread: "Open in Gmail" for the rare thing MeeMail can't do yet, and
// "Notify me about this sender" (a VIP change the owner makes, off by default). The Gmail link
// opens in a new tab and takes nothing with it (noopener, no referrer).
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { BellRing, ExternalLink } from "lucide-react";
import type { CardView } from "@shared/api";
import { gmailThreadUrl } from "@shared/push";
import { cn } from "@web/lib/cn";
import { useCardThread } from "@web/lib/feedQueries";
import { screenTarget, type ScreenTarget } from "@web/screener/screener";
import { MENU_ITEM_CLASS } from "@web/ui/Menu";
import { useSenderNotifyToggle, useVipMatch } from "./senderNotify";

export function OpenInGmail({
  accountEmail,
  threadKey,
  rfc822MessageId,
  variant = "link",
  className,
}: {
  accountEmail: string | null | undefined;
  threadKey: string | null;
  /** The original message's Message-ID: the link then finds that exact message. */
  rfc822MessageId?: string | null | undefined;
  /** "button" matches the card's row of email actions; "link" is the quiet text link; "menu" is a row
   * in a Menu. */
  variant?: "link" | "button" | "menu";
  className?: string;
}) {
  const href = gmailThreadUrl(accountEmail, threadKey, rfc822MessageId);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="open-in-gmail"
      title="Find this email in Gmail"
      className={cn(
        variant === "menu"
          ? MENU_ITEM_CLASS
          : variant === "button"
            ? "mm-tap inline-flex h-8 items-center gap-1.5 rounded-lg border border-line-strong px-3 text-sm font-medium text-ink-2 hover:bg-sunk"
            : "mm-tap inline-flex items-center gap-1.5 text-sm text-muted underline-offset-2 hover:text-ink hover:underline",
        className,
      )}
    >
      <ExternalLink aria-hidden className="size-3.5" />
      Open in Gmail
    </a>
  );
}

export function NotifySenderSwitch({ sender }: { sender: ScreenTarget }) {
  const match = useVipMatch(sender.address);
  const toggle = useSenderNotifyToggle();
  const [busy, setBusy] = useState(false);
  if (!match) return null;
  const label = sender.name || sender.address;
  if (match.kind === "domain")
    return (
      <span
        className="inline-flex items-center gap-1.5 text-sm text-muted"
        data-testid="notify-sender"
      >
        <BellRing aria-hidden className="size-3.5" />
        Everyone at {match.domain} is a{" "}
        <Link to="/settings/vips" className="underline underline-offset-2 hover:text-ink">
          VIP
        </Link>
      </span>
    );
  const on = match.kind === "address";
  return (
    <label
      className="inline-flex cursor-pointer items-center gap-2 text-sm text-muted"
      title="Adds them to your VIPs: their mail skips the wait, and reaches your phone when it needs you."
      data-testid="notify-sender"
    >
      <input
        type="checkbox"
        role="switch"
        checked={on}
        disabled={busy}
        onChange={async (e) => {
          setBusy(true);
          await toggle({ address: sender.address, label }, e.target.checked);
          setBusy(false);
        }}
        className="size-3.5 accent-[var(--accent)]"
      />
      Notify me about this sender
    </label>
  );
}

function CardNotify({ sender }: { sender: ScreenTarget }) {
  // No VIP match (an unknown sender, still loading): no row, so no empty gap on the card.
  if (!useVipMatch(sender.address)) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="card-sender-links">
      <NotifySenderSwitch sender={sender} />
    </div>
  );
}

/** Who wrote the card's email, once its thread has loaded (null before, and with no thread). */
export function useCardSender(
  card: Pick<CardView, "id" | "threadKey">,
): (ScreenTarget & { threadKey: string; accountEmail: string }) | null {
  const thread = useCardThread(card.id, card.threadKey, false);
  if (!card.threadKey || thread.data?.thread.key !== card.threadKey) return null;
  const sender = screenTarget(thread.data);
  if (!sender) return null;
  return { ...sender, threadKey: card.threadKey, accountEmail: thread.data.thread.accountEmail };
}

/**
 * The row under a decision card: needs the card's thread to know who wrote. Open in Gmail sits
 * in the card's More menu with its other email actions, not here.
 */
export function CardSenderLinks({ card }: { card: CardView }) {
  const sender = useCardSender(card);
  if (!sender) return null;
  return <CardNotify key={sender.address} sender={sender} />;
}
